// UPPDRAG-31 — pure helpers for the schedule-grid drag-drop flow.
//
// Three concerns:
//
// 1. validateDropZone: given a candidate drop position (court + slot
//    index) and the dragged booking's duration, decide whether the
//    drop is valid. Used both for visual feedback during drag (green
//    vs red highlight on the proposed slot range) and for the hard
//    rejection on drop.
//
// 2. computeDropTargetSlots: returns the array of [slotIdx ... slotIdx +
//    slotCount - 1] indexes that would be occupied. Used by cells to
//    decide whether they're inside the proposed range.
//
// 3. resolveInitialTargetFromDrop: turns a drop's (court, slot) into
//    the { courtId, date, time } shape that MoveBookingDialog accepts
//    as `initialTarget` for pre-fill. Centralised here so the dialog
//    test cases don't need to know about timeSlots Date objects.
//
// Why pure helpers? AdminScheduleGrid is 1000+ lines, drag-drop is
// already a tangled mess of refs / state / pointer events. Extracting
// the math + validation here so vitest can guard against regressions
// without standing up a full DOM.

import { addMinutes, format } from "date-fns";
import { checkSlotAvailability } from "@/lib/bookingConflict";

export type DropInvalidReason = "out_of_bounds" | "slot_occupied";

export interface DropZoneValidation {
  valid: boolean;
  /** When invalid, why. Lets the UI pick a Swedish message. */
  reason?: DropInvalidReason;
}

export interface ValidateDropZoneArgs {
  /** Court the user is hovering over. */
  targetCourtId: string;
  /** Index of the first slot the booking would occupy. */
  targetSlotIdx: number;
  /** How many slots the booking spans (duration / timeStep). */
  slotCount: number;
  /** Total slots in the day grid (timeSlots.length). */
  totalSlotsInDay: number;
  /** Wall-clock Date of the first proposed slot (timeSlots[targetSlotIdx]). */
  targetSlotStart: Date;
  /** Booking's duration in minutes — used to compute the proposed end. */
  durationMinutes: number;
  /** Existing bookings to check against (same shape as checkSlotAvailability). */
  bookings: Array<{ court_id: string; start_time: string; end_time: string; status: string; id: string }>;
  /** Id of the booking being dragged — excluded from the conflict check. */
  draggedBookingId: string;
}

/**
 * Decide whether a candidate drop position is valid.
 *
 * Two failure modes:
 *   - `out_of_bounds`: the booking's duration would extend past the
 *     last slot of the day (e.g. dragging a 60-min booking onto the
 *     final 30-min slot).
 *   - `slot_occupied`: any of the slots in [targetSlotIdx, targetSlotIdx
 *     + slotCount) overlap an existing confirmed booking on the target
 *     court (excluding the dragged booking itself).
 */
export function validateDropZone(args: ValidateDropZoneArgs): DropZoneValidation {
  // 1. Out-of-bounds — booking can't extend past the last grid slot
  if (args.targetSlotIdx + args.slotCount > args.totalSlotsInDay) {
    return { valid: false, reason: "out_of_bounds" };
  }
  // 2. Conflict — checkSlotAvailability already handles the time-range
  //    overlap math (excluding the dragged booking via draggedBookingId).
  const newEnd = addMinutes(args.targetSlotStart, args.durationMinutes);
  const { available } = checkSlotAvailability(
    args.targetCourtId,
    args.targetSlotStart,
    newEnd,
    args.bookings,
    args.draggedBookingId,
  );
  if (!available) {
    return { valid: false, reason: "slot_occupied" };
  }
  return { valid: true };
}

/**
 * Returns the inclusive set of slot indexes the booking would occupy
 * if dropped at `targetSlotIdx`. Cells use this to decide whether to
 * paint themselves as part of the drop zone (green/red).
 *
 * Always returns `slotCount` indexes — does NOT clip at totalSlotsInDay
 * so the UI can still show the "would extend past the end" overhang as
 * red (or just paint the in-bounds part — caller's choice).
 */
export function computeDropTargetSlots(args: {
  targetSlotIdx: number;
  slotCount: number;
}): number[] {
  const slots: number[] = [];
  for (let i = 0; i < args.slotCount; i++) {
    slots.push(args.targetSlotIdx + i);
  }
  return slots;
}

/**
 * Translate a drop's grid coordinates into the `initialTarget` shape
 * MoveBookingDialog accepts for pre-fill. Date is the YYYY-MM-DD of the
 * target slot in the local timezone of the slot Date object (which is
 * what the schedule grid uses).
 */
export function resolveInitialTargetFromDrop(args: {
  targetCourtId: string;
  targetSlotStart: Date;
}): { courtId: string; date: string; time: string } {
  return {
    courtId: args.targetCourtId,
    date: format(args.targetSlotStart, "yyyy-MM-dd"),
    time: format(args.targetSlotStart, "HH:mm"),
  };
}

// ─── Lager 3a-fix — canonical validate+route pipeline ───────────────────
//
// Extracted from AdminScheduleGrid's handleBookingDrop (drag path) so both
// drag-drop and click-to-move route IDENTICALLY through the same precedence
// chain. Before this extraction, the click-to-move intercept in
// handleSlotClick only ran the P3 price-guard then went straight to
// `setMoveBooking` ("regular path") — bypassing validateDropZone (collision
// + out-of-bounds, the 90-min-on-30-min-free slot bug) AND missing four
// specialty routes (linked league-match, canonical open-match, group,
// recurring, legacy-activity).
//
// `resolveAndRouteMove` is a PURE function — it returns a `MoveResolution`
// decision; the caller performs setState/toast. This makes both call-sites
// trivially testable + keeps the routing logic single-source-of-truth.
//
// P3 price-guard intentionally stays OUTSIDE this function — it's a
// click-specific affordance (drag-drop already shows price delta via
// MoveBookingDialog confirmation; click-to-move lacks the drag-zone visual
// cue + must defensively block until Lager 3b ships per-seat MoveBookingDialog).

export type DropTarget = ReturnType<typeof resolveInitialTargetFromDrop>;

export type MoveResolution =
  | { kind: "blocked"; reason: DropInvalidReason }
  | { kind: "league"; dropTarget: DropTarget; matchId: string }
  | { kind: "activity"; dropTarget: DropTarget; activityId: string }
  | { kind: "group"; dropTarget: DropTarget }
  | { kind: "recurring"; dropTarget: DropTarget }
  | { kind: "legacy_activity"; dropTarget: DropTarget }
  | { kind: "regular"; dropTarget: DropTarget };

/**
 * Booking-shape consumed by the move pipeline. Intentionally minimal —
 * only the fields that drive routing decisions. Callers cast their
 * fully-loaded ScheduleBooking to this shape; extra fields are ignored.
 */
export interface MovePipelineBooking {
  id: string;
  duration_minutes: number;
  booking_group_id?: string | null;
  activity_id?: string | null;
  is_open_match?: boolean | null;
}

export interface ResolveAndRouteMoveArgs {
  booking: MovePipelineBooking;
  targetCourtId: string;
  targetSlotIdx: number;
  targetSlotStart: Date;
  slotCount: number;
  totalSlotsInDay: number;
  bookings: Array<{
    court_id: string;
    start_time: string;
    end_time: string;
    status: string;
    id: string;
  }>;
  // Pure resolver-callbacks — caller provides them. Allows tests to stub
  // without React/Supabase scaffolding. In production these come from
  // useScheduleGridData (linked-FK normalizers) + getBookingCategory.
  getLinkedLeagueMatch: (b: MovePipelineBooking) => { id: string } | null | undefined;
  getLinkedOpenMatch: (b: MovePipelineBooking) => { id: string } | null | undefined;
  getBookingCategory: (b: MovePipelineBooking) => string;
}

/**
 * Run the full validate + route pipeline for a candidate move target.
 *
 * Precedence (identical to drag-vägens pre-Lager-3a-fix order):
 *   1. validateDropZone → blocked if out_of_bounds OR slot_occupied
 *   2. linked_league_match → MatchDetailModal route
 *   3. linked_open_match  → ActivityDetailModal route
 *   4. booking_group_id    → MoveBookingDialog (group context)
 *   5. recurring category  → SubscriptionMoveDialog (recurring)
 *   6. is_open_match / activity_id → SubscriptionMoveDialog (legacy activity)
 *   7. regular → MoveBookingDialog
 *
 * Returns a discriminated union so the caller can switch on `kind` and
 * apply the corresponding setState + (for blocked) toast.
 */
export function resolveAndRouteMove(args: ResolveAndRouteMoveArgs): MoveResolution {
  // Step 1 — validation. Re-uses the same helper drag's drop-zone feedback
  // uses, so the rejection here can never disagree with the green/red hint
  // operators see under their cursor during drag.
  const validation = validateDropZone({
    targetCourtId: args.targetCourtId,
    targetSlotIdx: args.targetSlotIdx,
    slotCount: args.slotCount,
    totalSlotsInDay: args.totalSlotsInDay,
    targetSlotStart: args.targetSlotStart,
    durationMinutes: args.booking.duration_minutes,
    bookings: args.bookings,
    draggedBookingId: args.booking.id,
  });
  if (!validation.valid) {
    return { kind: "blocked", reason: validation.reason ?? "slot_occupied" };
  }

  // Step 2 — drop target (for all non-blocked outcomes).
  const dropTarget = resolveInitialTargetFromDrop({
    targetCourtId: args.targetCourtId,
    targetSlotStart: args.targetSlotStart,
  });

  // Steps 3-7 — routing by booking kind. Order matters: most-specific first.
  const llm = args.getLinkedLeagueMatch(args.booking);
  if (llm?.id) return { kind: "league", dropTarget, matchId: llm.id };

  const lom = args.getLinkedOpenMatch(args.booking);
  if (lom?.id) return { kind: "activity", dropTarget, activityId: lom.id };

  if (args.booking.booking_group_id) {
    return { kind: "group", dropTarget };
  }
  if (args.getBookingCategory(args.booking) === "recurring") {
    return { kind: "recurring", dropTarget };
  }
  if (args.booking.activity_id || args.booking.is_open_match) {
    return { kind: "legacy_activity", dropTarget };
  }
  return { kind: "regular", dropTarget };
}
